import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loader from '../../Components/utils/Loader';
import SEO from '../../Components/utils/SEO';
import { stripHtml, truncateText } from '../../utils/seoUtils';
import { SEO_SITE_NAME, SEO_SITE_URL, SEO_DEFAULT_IMAGE } from '../../config/seoConfig';

const ArticleDetailPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                setIsLoading(true);
                const { data } = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/blogs/${slug}`);
                if (data.success) {
                    setBlog(data.blog);
                } else {
                    setError('Blog not found');
                }
            } catch (error) {
                setError('Failed to fetch blog post');
            } finally {
                setIsLoading(false);
            }
        };
        fetchBlog();
    }, [slug]);

    const canonicalPath = `/articleDetail/${slug}`;
    const seoDescription = blog?.content
        ? truncateText(stripHtml(blog.content), 160)
        : 'Read the latest articles and recipes from Chocowi.';
    const seoImage = blog?.image || SEO_DEFAULT_IMAGE;
    const schema = blog ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: blog.title,
        image: blog.image ? [blog.image] : undefined,
        datePublished: blog.date ? new Date(blog.date).toISOString() : undefined,
        dateModified: blog.updatedAt ? new Date(blog.updatedAt).toISOString() : undefined,
        author: { "@type": "Organization", name: SEO_SITE_NAME },
        publisher: {
            "@type": "Organization",
            name: SEO_SITE_NAME,
            logo: { "@type": "ImageObject", url: `${SEO_SITE_URL}/chocowi-logo.png` }
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SEO_SITE_URL}${canonicalPath}` }
    } : null;

    if (isLoading) {
        return (
            <>
                <SEO title="Article" description="Loading article" canonicalPath={canonicalPath} noIndex />
                <Loader type="simple" />
            </>
        );
    }

    if (error || !blog) {
        return (
            <div className="errorContainer">
                <SEO title="Article not found" description="This article could not be found." canonicalPath="/blogs" noIndex />
                <p>{error || 'Article not found'}</p>
                <button onClick={() => navigate('/blog')}>Back to Articles</button>
            </div>
        );
    }

    return (
        <div className="articleDetailContainer">
            <SEO
                title={blog.title}
                description={seoDescription}
                canonicalPath={canonicalPath}
                image={seoImage}
                type="article"
                schema={schema}
            />
            <div className="articleHeader">
                <span className="categoryLabel">{blog.category}</span>
                <h1 className="articleDetailTitle">{blog.title}</h1>
                <div className="articleMeta">
                    <span className="articleDate">{new Date(blog.date).toLocaleDateString()}</span>
                </div>
            </div>

            {blog.image && (
                <div className="mainImageContainer">
                    <img src={blog.image} alt={blog.title} className="mainImage" />
                </div>
            )}

            <div className="mainContent">
                <div dangerouslySetInnerHTML={{ __html: blog.content }} />
            </div>
        </div>
    );
};


export default ArticleDetailPage;